import { Box, Container, Grid, Skeleton, Stack, Typography, styled, useTheme } from '@mui/material';
import { get } from 'lodash';
import numeral from 'numeral';
import queryString from 'query-string';
import update from 'immutability-helper';
import { ChangeEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Components
import FilterPeopleProfile from 'src/features/PeopleProfiles/Filter';
import PeopleProfileDetail from 'src/features/PeopleProfiles/PeopleProfileDetail';

// Utilities
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryListPeople } from 'src/services/queries';
import { useMutateCreateBookmark, useMutateUpdateBookmarkByDataUuid } from 'src/services/mutate/useMutateBookmark';
import { BookmarkTypes } from 'src/model/bookmark';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import FormBookmarkNote from 'src/features/Bookmark/FormBookmarkNote';
import PeopleProfileCard from 'src/features/PeopleProfiles/PeopleProfileCard';
import { LIMIT_GET_LIST_PEOPLE } from 'src/utils/constants/config';
import NoDataSearchPeople from 'src/features/PeopleProfiles/NoDataSearchPeople';
import React from 'react';
import StyledPagination from 'src/components/common/StyledPagination';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(7),
  marginTop: 8,
}));

const PeopleProfiles = () => {
  const query = useQueryParams();
  const [selected, setSelected] = useState(undefined as undefined | IPeopleProfile);
  const [isBookmarkList, setIsBookmarkList] = useState<Record<string, boolean>>({}); // Fake update bookmark in FE
  const [noteBookmarkList, setNoteBookmarkList] = useState<Record<string, string>>({}); // Fake update bookmark in FE
  /**  When choose company to create/update data bookmark => Store to this state */
  const [bookmarkNoteForm, setBookmarkNoteForm] = useState<{
    isOpen: boolean;
    peopleId: string;
    peopleBookmarkNote: string;
    peopleHasBookmark: boolean;
  }>({ isOpen: false, peopleId: '', peopleBookmarkNote: '', peopleHasBookmark: false });
  const { mutateAsync: createBookmarkMutate, isPending: isPendingCreateBookmark } = useMutateCreateBookmark();
  const { mutateAsync: updateBookmarkMutate, isPending: isPendingUpdateBookmark } = useMutateUpdateBookmarkByDataUuid();

  const [, setSearchParams] = useSearchParams();
  const { data, isFetching } = useQueryListPeople(queryString.stringify(query));
  const { data: listPeople = [] } = data;

  useEffect(() => {
    const initNoteBookmarkList = listPeople.reduce((acc, people) => {
      acc = {
        ...acc,
        [people.id || '']: people?.bookmark_note || '',
      };
      return acc;
    }, {});
    setNoteBookmarkList(initNoteBookmarkList);
  }, [listPeople]);

  const checkPeopleIsBookmark = (people: IPeopleProfile) => {
    // Kiểm tra cả dữ liệu trả về từ server và state dùng để fake update trên FE
    return people.has_bookmark || get(isBookmarkList, people.id || '', false);
  };

  const updateNoteBookmark = (note: string) => {
    setNoteBookmarkList((prev) => {
      const newNoteBookmarkList = update(prev, {
        [bookmarkNoteForm?.peopleId || '']: {
          $set: note,
        },
      });
      return newNoteBookmarkList;
    });
  };

  const handleOpenBookmarkNoteForm = (peopleId: string, peopleHasBookmark: boolean) => {
    setBookmarkNoteForm({
      isOpen: true,
      peopleId,
      peopleBookmarkNote: get(noteBookmarkList, peopleId, ''),
      peopleHasBookmark,
    });
  };

  const handleCloseBookmarkNoteForm = () => {
    setBookmarkNoteForm({
      isOpen: false,
      peopleId: '',
      peopleBookmarkNote: '',
      peopleHasBookmark: false,
    });
  };

  const handleSubmitBookmarkNoteForm = async () => {
    const response = bookmarkNoteForm?.peopleHasBookmark
      ? await updateBookmarkMutate({
          type: BookmarkTypes.People,
          dataUuid: bookmarkNoteForm?.peopleId || '',
          body: {
            note: get(noteBookmarkList, bookmarkNoteForm.peopleId, ''),
          },
        })
      : await createBookmarkMutate({
          type: BookmarkTypes.People,
          body: {
            data_uuid: bookmarkNoteForm?.peopleId || '',
            note: get(noteBookmarkList, bookmarkNoteForm.peopleId, ''),
          },
        });

    if (response?.status_code === SUCCESS_CODE.CREATE) {
      setIsBookmarkList((prev) => {
        const newIsBookmarkList = update(prev, {
          [bookmarkNoteForm?.peopleId || '']: {
            $set: true,
          },
        });
        return newIsBookmarkList;
      });
    }

    handleCloseBookmarkNoteForm();
  };

  const renderLoading = () => (
    <Grid container spacing={2}>
      {Array(Number(LIMIT_GET_LIST_PEOPLE))
        .fill('')
        .map((_, index) => (
          <Grid key={`skeleton-profile-company-${index}`} sm={12} md={6} lg={4} xl={4} item>
            <SkeletonProfile />
          </Grid>
        ))}
    </Grid>
  );

  return (
    <StyledContainer maxWidth="xl">
      <Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h5" gutterBottom>
            People Profile Search Result
          </Typography>
          <Typography variant="body2" gutterBottom>
            We found{' '}
            <Typography variant="subtitle2" component="span">
              {numeral(get(data, 'total_records', 0) || 0).format('0,0')} profiles matching
            </Typography>{' '}
            your search conditions
          </Typography>
        </Stack>
        <Box my={2}>
          <SectionErrorBoundary resetKey="FilterPeopleProfile" sectionName="FilterPeopleProfile">
            <FilterPeopleProfile />
          </SectionErrorBoundary>
        </Box>

        <SectionErrorBoundary resetKey="PeopleProfilesResults" sectionName="PeopleProfilesResults">
          {isFetching ? (
            renderLoading()
          ) : (
            <React.Fragment>
              {listPeople.length === 0 ? (
                <NoDataSearchPeople />
              ) : (
                <Stack direction="row" spacing={3}>
                  <Stack width="70%">
                    <Grid container spacing={2}>
                      {listPeople.map((people, index) => {
                        return (
                          <Grid key={index + (people?.id || '')} item sm={12} md={6} lg={6} xl={6}>
                            <PeopleProfileCard
                              isBookmark={checkPeopleIsBookmark(people)} // Fake update bookmark in FE
                              onSelect={() => {
                                setSelected(people);
                              }}
                              selected={selected}
                              people={people}
                              handleOpenBookmarkNoteForm={handleOpenBookmarkNoteForm}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                    {listPeople.length > 0 && (
                      <Stack mt={2} alignItems="center">
                        <StyledPagination
                          page={Number(query.offset) + 1}
                          total_pages={data.total_pages}
                          onChange={function (_: ChangeEvent<unknown>, value: number): void {
                            setSearchParams((searchParams) => {
                              searchParams.set('offset', String(value - 1));
                              return searchParams;
                            });
                          }}
                        />
                      </Stack>
                    )}
                  </Stack>
                  <Stack width="30%">
                    {selected && (
                      <PeopleProfileDetail
                        id={selected?.id || ''}
                        isBookmark={checkPeopleIsBookmark(selected)}
                        handleOpenBookmarkNoteForm={handleOpenBookmarkNoteForm}
                      />
                    )}
                  </Stack>
                </Stack>
              )}
            </React.Fragment>
          )}
          <FormBookmarkNote
            open={bookmarkNoteForm.isOpen}
            handleClose={() => {
              handleCloseBookmarkNoteForm();
              // reset old note
              updateNoteBookmark(bookmarkNoteForm?.peopleBookmarkNote || '');
            }}
            handleSubmit={() => handleSubmitBookmarkNoteForm()}
            value={get(noteBookmarkList, bookmarkNoteForm.peopleId, '')}
            onChange={(value) => updateNoteBookmark(value)}
            isLoading={isPendingCreateBookmark || isPendingUpdateBookmark}
          />
        </SectionErrorBoundary>
      </Stack>
    </StyledContainer>
  );
};

export default PeopleProfiles;

const SkeletonProfile = () => {
  const theme = useTheme();
  return (
    <Box
      display={'flex'}
      flexDirection="column"
      gap={1}
      padding={1.5}
      borderRadius={theme.shape.borderRadius}
      bgcolor={theme.palette.background.default}
    >
      <Skeleton animation="wave" />
      <Skeleton animation="wave" variant="rectangular" height={24} width={60} />
      <Skeleton animation="wave" variant="rectangular" height={24} width={60} />
      <Skeleton animation="wave" variant="rectangular" height={24} width={60} />
    </Box>
  );
};
